import { useEffect, useRef, useState } from 'react'
import { LOCALES, LOCALE_META } from '@/i18n/locales'
import type { Locale } from '@/i18n/locales'
import { useSite } from '@/app/site-context'
import type { RouteKind } from '@/app/routes'

interface LanguageSwitcherProps {
  variant?: 'bar' | 'stack'
  onNavigate?: (href: string) => void
}

export function LanguageSwitcher({ variant = 'bar', onNavigate }: LanguageSwitcherProps) {
  const { locale, dictionary, route, pathFor } = useSite()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const kind: RouteKind = route.kind === 'notFound' ? 'home' : route.kind

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node) === false) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const linkFor = (target: Locale) => pathFor(kind, target)

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, target: Locale) => {
    if (onNavigate === undefined) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    setOpen(false)
    onNavigate(linkFor(target))
  }

  if (variant === 'stack') {
    return (
      <nav className="lang lang--stack" aria-label={dictionary.nav.languageLabel}>
        {LOCALES.map((item) => (
          <a
            key={item}
            href={linkFor(item)}
            hrefLang={LOCALE_META[item].hrefLang}
            lang={LOCALE_META[item].htmlLang}
            className="lang__option"
            aria-current={item === locale ? 'true' : undefined}
            onClick={(event) => {
              handleClick(event, item)
            }}
          >
            <span className="lang__code">{LOCALE_META[item].label}</span>
            <span className="lang__name">{LOCALE_META[item].endonym}</span>
          </a>
        ))}
      </nav>
    )
  }

  return (
    <div className="lang" ref={containerRef}>
      <button
        type="button"
        className="lang__trigger"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={`${LOCALE_META[locale].label}, ${dictionary.nav.languageLabel}: ${LOCALE_META[locale].endonym}`}
        onClick={() => {
          setOpen((value) => !value)
        }}
      >
        <span aria-hidden="true" className="lang__globe">
          <svg viewBox="0 0 24 24" width="15" height="15" focusable="false">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
            <path
              d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </span>
        {LOCALE_META[locale].label}
      </button>
      {open ? (
        <nav className="lang__menu" aria-label={dictionary.nav.languageLabel}>
          {LOCALES.map((item) => (
            <a
              key={item}
              href={linkFor(item)}
              hrefLang={LOCALE_META[item].hrefLang}
              lang={LOCALE_META[item].htmlLang}
              className="lang__option"
              aria-current={item === locale ? 'true' : undefined}
              onClick={(event) => {
                handleClick(event, item)
              }}
            >
              <span className="lang__code">{LOCALE_META[item].label}</span>
              <span className="lang__name">{LOCALE_META[item].endonym}</span>
            </a>
          ))}
        </nav>
      ) : null}
    </div>
  )
}
